import { ProgressBar } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";

import { useIsWorkplaceReviewUXUpdateEnabled } from "../hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { type PostShiftReviewStep, type StepAction } from "../types";

export interface PostShiftReviewHeaderProps {
  currentStep: PostShiftReviewStep;
  computedSteps: PostShiftReviewStep[];
  onBackOrClose: (action: StepAction) => void;
}

export function PostShiftReviewHeader({
  currentStep,
  computedSteps,
  onBackOrClose,
}: PostShiftReviewHeaderProps) {
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();
  const currentIndex = computedSteps.indexOf(currentStep);

  function getHeaderConfig() {
    if (isWriteReviewUiUpdateEnabled) {
      if (currentStep === "end") {
        return null;
      }

      // First step gets close button (X) to dismiss the flow
      if (currentIndex === 0) {
        return {
          iconType: "close",
          action: { type: "CLOSE_FLOW" },
        } as const;
      }

      return {
        iconType: "chevron-left",
        action: { type: "BACK_STEP" },
      } as const;
    }

    // Old UI: pick-rating has no button (first step), write-review gets close, end has no button
    // FIXME: Automatically disabled during ESLint config update
    switch (currentStep) {
      case "pick-rating": {
        return null;
      }

      case "write-review": {
        return {
          iconType: "close",
          action: { type: "CLOSE_FLOW" },
        } as const;
      }

      case "end": {
        return null;
      }

      default: {
        return {
          iconType: "chevron-left",
          action: { type: "BACK_STEP" },
        } as const;
      }
    }
  }

  const config = getHeaderConfig();

  const progressTotal = computedSteps.length - 1; // Exclude "end" step from progress calculation
  const shouldShowProgressBar = currentStep !== "end" && progressTotal > 1;
  const progressCompleted = Math.min(currentIndex + 1, progressTotal);

  return (
    <Stack direction="row" alignItems="center" sx={{ px: 4, py: 2 }}>
      {/* Always reserve space for the button to prevent height jump */}
      <Box sx={{ height: 40, display: "flex", alignItems: "center" }}>
        {config && (
          <IconButton
            variant="outlined"
            size="small"
            iconType={config.iconType}
            sx={{ mr: 4 }}
            onClick={() => {
              onBackOrClose(config.action);
            }}
          />
        )}
      </Box>

      {shouldShowProgressBar && (
        <Box sx={{ flex: 1, mx: 3 }}>
          <ProgressBar completed={progressCompleted} total={progressTotal} />
        </Box>
      )}
    </Stack>
  );
}
